import type { TaskAgenda } from '@/types/taskAgenda';

/** yyyy-MM-dd in America/Sao_Paulo */
export function todayYmdSaoPaulo(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Sao_Paulo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

/** yyyy-MM-dd do dia anterior em America/Sao_Paulo */
export function yesterdayYmdSaoPaulo(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Sao_Paulo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(Date.now() - 24 * 60 * 60 * 1000));
}

/**
 * Verifica se existe algum dia com execucao agendada (conforme allowedWeekdays) entre
 * o dia seguinte a lastRunDate e today (exclusive). Limita retroativamente a 7 dias.
 * Retorna true se algum dia programado ficou sem execucao.
 */
export function wasAnyScheduledDayMissed(
  lastRunDate: string | null | undefined,
  today: string,
  allowedWeekdays: number[] | null
): boolean {
  if (!lastRunDate) return true;
  if (lastRunDate >= today) return false;

  // Limita o retroativo a 7 dias para evitar iteracoes longas
  const cutoff = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Sao_Paulo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000));
  const startFrom = lastRunDate < cutoff ? cutoff : lastRunDate;

  // Itera dia a dia de startFrom+1 ate today (exclusive)
  let cursor = new Date(`${startFrom}T12:00:00-03:00`);
  const limit = new Date(`${today}T00:00:00-03:00`);
  cursor = new Date(cursor.getTime() + 24 * 60 * 60 * 1000);

  while (cursor < limit) {
    const wd = cursor.getDay(); // 0=Dom ... 6=Sab (mesmo indice do Date.getDay())
    if (allowedWeekdays === null || allowedWeekdays.includes(wd)) {
      return true; // dia agendado nao executou
    }
    cursor = new Date(cursor.getTime() + 24 * 60 * 60 * 1000);
  }
  return false;
}

/** Primeiro dia do mes corrente em America/Sao_Paulo (yyyy-MM-dd). */
export function startOfMonthYmdSaoPaulo(): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Sao_Paulo',
    year: 'numeric',
    month: '2-digit',
  }).formatToParts(new Date());
  const y = parts.find((p) => p.type === 'year')?.value;
  const m = parts.find((p) => p.type === 'month')?.value;
  if (!y || !m) return todayYmdSaoPaulo();
  return `${y}-${m}-01`;
}

/**
 * Execucoes da agenda ficam sempre dentro do mes corrente (SP): nao processa meses anteriores nem futuro.
 * Cruza o intervalo desejado (ex.: ultimos N dias) com [dia 1 ... hoje].
 */
export function clampTaskAgendaRangeToCurrentMonthSp(
  dataInicio: string | null | undefined,
  dataFim: string | null | undefined
): { dataInicio: string; dataFim: string } {
  const monthStart = startOfMonthYmdSaoPaulo();
  const today = todayYmdSaoPaulo();
  const di = (dataInicio && dataInicio.trim()) || monthStart;
  const df = (dataFim && dataFim.trim()) || today;
  const start = di < monthStart ? monthStart : di;
  const end = df > today ? today : df;
  if (start > end) {
    return { dataInicio: monthStart, dataFim: today };
  }
  return { dataInicio: start, dataFim: end };
}

export function lastRunYmdSaoPaulo(iso: string | null | undefined): string | null {
  if (!iso) return null;
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Sao_Paulo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(iso));
}

export function isWithinAgendaDateWindow(agenda: TaskAgenda): boolean {
  const cur = todayYmdSaoPaulo();
  if (agenda.data_inicio) {
    if (cur < agenda.data_inicio) return false;
  }
  if (agenda.data_fim) {
    if (cur > agenda.data_fim) return false;
  }
  return true;
}

/** Dia da semana em America/Sao_Paulo (0 = domingo ... 6 = sabado, igual a Date.getDay()). */
export function currentWeekdaySaoPaulo(): number {
  const wd = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Sao_Paulo',
    weekday: 'long',
  }).formatToParts(new Date());
  const v = wd.find((p) => p.type === 'weekday')?.value;
  const map: Record<string, number> = {
    Sunday: 0,
    Monday: 1,
    Tuesday: 2,
    Wednesday: 3,
    Thursday: 4,
    Friday: 5,
    Saturday: 6,
  };
  return v ? (map[v] ?? 0) : 0;
}

/** Lista de dias permitidos ou null = todos os dias. */
export function getScheduleWeekdays(agenda: TaskAgenda): number[] | null {
  const w = agenda.schedule_weekdays;
  if (!w || !Array.isArray(w) || w.length === 0) return null;
  const uniq = [...new Set(w.map((d) => Number(d)).filter((d) => d >= 0 && d <= 6))].sort((a, b) => a - b);
  return uniq.length ? uniq : null;
}

export function parseHm(s: string): { h: number; m: number } | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(s.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h < 0 || h > 23 || min < 0 || min > 59) return null;
  return { h, m: min };
}

/** Interpreta texto da tela: "08:00, 21:10" -> lista normalizada HH:mm. */
export function parseDailyTimesSpInput(input: string): string[] | null {
  const parts = input
    .split(/[,;\n]+/)
    .map((p) => p.trim())
    .filter(Boolean);
  const keys: string[] = [];
  const seen = new Set<string>();
  for (const p of parts) {
    const parsed = parseHm(p);
    if (!parsed) return null;
    const key = `${String(parsed.h).padStart(2, '0')}:${String(parsed.m).padStart(2, '0')}`;
    if (seen.has(key)) continue;
    seen.add(key);
    keys.push(key);
  }
  if (keys.length === 0) return null;
  keys.sort();
  return keys;
}

/** Horarios do dia (SP); `payload.dailyTimesSp` ou colunas hora/minuto. */
export function getDailySpSlots(agenda: TaskAgenda): { hour: number; minute: number; key: string }[] {
  const raw = agenda.payload?.dailyTimesSp;
  if (Array.isArray(raw) && raw.length > 0) {
    const out: { hour: number; minute: number; key: string }[] = [];
    const seen = new Set<string>();
    for (const item of raw) {
      if (typeof item !== 'string') continue;
      const parsed = parseHm(item);
      if (!parsed) continue;
      const key = `${String(parsed.h).padStart(2, '0')}:${String(parsed.m).padStart(2, '0')}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ hour: parsed.h, minute: parsed.m, key });
    }
    if (out.length > 0) {
      return out.sort((a, b) => a.hour * 60 + a.minute - (b.hour * 60 + b.minute));
    }
  }
  const hour = Number(agenda.schedule_hour_sp ?? 20);
  const minute = Number(agenda.schedule_minute ?? 0);
  const key = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
  return [{ hour, minute, key }];
}

/** Janela primaria em minutos apos o horario (tick a cada 30 s). */
const DAILY_SP_SLOT_GRACE_MINUTES = 25;

/**
 * Disparo diario no fuso America/Sao_Paulo com tres camadas de protecao contra execucoes perdidas:
 *
 * 1. Janela primaria - executa durante os primeiros GRACE minutos apos o horario configurado
 *    (ex.: 23:00 dispara ate 23:25).
 *
 * 2. Catch-up no mesmo dia - se a janela primaria foi perdida (browser estava fechado),
 *    o job dispara assim que o usuario abre o app em qualquer momento restante do mesmo dia,
 *    desde que o horario agendado ja tenha passado.
 *
 * 3. Catch-up entre dias - ao abrir o app num dia posterior, detecta se algum dia
 *    programado (respeitando schedule_weekdays) ficou sem execucao nos ultimos 7 dias e,
 *    em caso positivo, dispara imediatamente como compensacao.
 *
 * Deduplicacao: payload.lastAutoRunSpDateBySlot[slotKey] garante no maximo uma execucao
 * automatica por slot por dia. "Executar agora" nao grava esse campo e nunca bloqueia o automatico.
 */
export function shouldRunDailySpSlotNow(
  agenda: TaskAgenda
): { should: true; slotKey: string } | { should: false } {
  if (!agenda.ativo) return { should: false };
  if (agenda.schedule_kind !== 'daily_sp') return { should: false };
  if (!isWithinAgendaDateWindow(agenda)) return { should: false };

  const allowedDays = getScheduleWeekdays(agenda);
  const todayWd = currentWeekdaySaoPaulo();
  const todayIsScheduled = allowedDays === null || allowedDays.includes(todayWd);

  const sp = new Date().toLocaleString('sv-SE', { timeZone: 'America/Sao_Paulo' });
  const timePart = sp.split(' ')[1] || '00:00:00';
  const [hStr, mStr] = timePart.split(':');
  const curH = Number(hStr);
  const curM = Number(mStr);
  const curTotalMin = curH * 60 + curM;
  const today = todayYmdSaoPaulo();

  const slots = getDailySpSlots(agenda);
  const bySlot = (agenda.payload?.lastAutoRunSpDateBySlot as Record<string, string> | undefined) ?? {};

  for (const slot of slots) {
    if (bySlot[slot.key] === today) continue; // ja rodou hoje neste slot

    const slotTotalMin = slot.hour * 60 + slot.minute;

    // 1. Janela primaria (comportamento original)
    const inPrimaryWindow =
      todayIsScheduled &&
      curH === slot.hour &&
      curM >= slot.minute &&
      curM <= slot.minute + DAILY_SP_SLOT_GRACE_MINUTES;

    // 2. Catch-up no mesmo dia (horario passou, browser estava fechado durante a janela primaria)
    const isSameDayCatchUp =
      todayIsScheduled && curTotalMin > slotTotalMin + DAILY_SP_SLOT_GRACE_MINUTES;

    // 3. Catch-up entre dias: algum dia agendado ficou sem execucao nos ultimos 7 dias?
    const lastRun = bySlot[slot.key] ?? null;
    const isCrossDayCatchUp = wasAnyScheduledDayMissed(lastRun, today, allowedDays);

    if (inPrimaryWindow || isSameDayCatchUp || isCrossDayCatchUp) {
      return { should: true, slotKey: slot.key };
    }
  }
  return { should: false };
}

/** Atualiza payload com a data (SP) do ultimo disparo automatico por horario. */
export function nextPayloadWithAutoSlot(
  agenda: TaskAgenda,
  automaticSlotKey?: string
): Record<string, unknown> {
  if (!automaticSlotKey) return { ...agenda.payload };
  const today = todayYmdSaoPaulo();
  const prev = (agenda.payload?.lastAutoRunSpDateBySlot as Record<string, string> | undefined) ?? {};
  return {
    ...agenda.payload,
    lastAutoRunSpDateBySlot: { ...prev, [automaticSlotKey]: today },
  };
}

/** @deprecated use shouldRunDailySpSlotNow */
export function shouldRunDailySpNow(agenda: TaskAgenda): boolean {
  return shouldRunDailySpSlotNow(agenda).should === true;
}
